import type { LabelPrintBatchStatus, PurchaseOrderStatus } from "@shared/types";
import { PurchaseOrderStatusKeys } from "@shared/types";

// ---------------------------------------------------------------------------
// Status display configuration
// ---------------------------------------------------------------------------

interface OrderStatusConfig {
  readonly labelKey: string;
  readonly className: string;
  readonly dotColor: string;
}

export const ORDER_STATUS_CONFIG: Record<PurchaseOrderStatus, OrderStatusConfig> = {
  WaitingToBeFulfilled: {
    labelKey: "views.orders.status.waitingToBeFulfilled",
    className: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400",
    dotColor: "bg-amber-500",
  },
  Fulfilled: {
    labelKey: "views.orders.status.fulfilled",
    className: "bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-400",
    dotColor: "bg-green-500",
  },
  Shipped: {
    labelKey: "views.orders.status.shipped",
    className: "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-400",
    dotColor: "bg-blue-500",
  },
  Delivered: {
    labelKey: "views.orders.status.delivered",
    className: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400",
    dotColor: "bg-emerald-600",
  },
  Canceled: {
    labelKey: "views.orders.status.canceled",
    className: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400",
    dotColor: "bg-gray-500",
  },
  AddressValidationError: {
    labelKey: "views.orders.status.addressValidationError",
    className: "bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-400",
    dotColor: "bg-orange-500",
  },
  Error: {
    labelKey: "views.orders.status.error",
    className: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400",
    dotColor: "bg-red-500",
  },
  LabelError: {
    labelKey: "views.orders.status.labelError",
    className: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400",
    dotColor: "bg-red-500",
  },
  ZenkraftAddressError: {
    labelKey: "views.orders.status.zenkraftAddressError",
    className: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400",
    dotColor: "bg-red-500",
  },
  DeliveryError: {
    labelKey: "views.orders.status.deliveryError",
    className: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400",
    dotColor: "bg-red-500",
  },
  ProductError: {
    labelKey: "views.orders.status.productError",
    className: "bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-400",
    dotColor: "bg-orange-500",
  },
  Publishing: {
    labelKey: "views.orders.status.publishing",
    className: "bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-400",
    dotColor: "bg-orange-500",
  },
  PartiallyDelivered: {
    labelKey: "views.orders.status.partiallyDelivered",
    className: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400",
    dotColor: "bg-emerald-500",
  },
  PartiallyFulfilled: {
    labelKey: "views.orders.status.partiallyFulfilled",
    className: "bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-400",
    dotColor: "bg-green-500",
  },
  PartiallyShipped: {
    labelKey: "views.orders.status.partiallyShipped",
    className: "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-400",
    dotColor: "bg-blue-500",
  },
} as const;

// ---------------------------------------------------------------------------
// Status filter definitions (matching client OrdersTable filters)
// ---------------------------------------------------------------------------

export interface OrderStatusFilter {
  readonly key: string;
  readonly labelKey: string;
  readonly statuses: readonly PurchaseOrderStatus[];
  readonly dotColor: string;
}

/** "all" filter includes all statuses visible in the client app */
const ALL_FILTER_STATUSES: readonly PurchaseOrderStatus[] = [
  "AddressValidationError",
  "Publishing",
  "ProductError",
  "Error",
  "WaitingToBeFulfilled",
  "Fulfilled",
  "Shipped",
  "Delivered",
  "LabelError",
  "DeliveryError",
  "ZenkraftAddressError",
  "Canceled",
] as const;

export const ORDER_STATUS_FILTERS: readonly OrderStatusFilter[] = [
  {
    key: "all",
    labelKey: "views.orders.filters.all",
    statuses: ALL_FILTER_STATUSES,
    dotColor: "bg-gray-400",
  },
  {
    key: "AddressValidationError",
    labelKey: "views.orders.filters.addressValidation",
    statuses: ["AddressValidationError", "Publishing", "ProductError", "Error"],
    dotColor: "bg-orange-500",
  },
  {
    key: "WaitingToBeFulfilled",
    labelKey: "views.orders.filters.waiting",
    statuses: ["WaitingToBeFulfilled"],
    dotColor: "bg-amber-500",
  },
  {
    key: "Fulfilled",
    labelKey: "views.orders.filters.fulfilled",
    statuses: ["Fulfilled"],
    dotColor: "bg-green-500",
  },
  {
    key: "Shipped",
    labelKey: "views.orders.filters.shipped",
    statuses: ["Shipped"],
    dotColor: "bg-blue-500",
  },
  {
    key: "Delivered",
    labelKey: "views.orders.filters.delivered",
    statuses: ["Delivered"],
    dotColor: "bg-emerald-600",
  },
  {
    key: "Error",
    labelKey: "views.orders.filters.error",
    statuses: ["LabelError", "ZenkraftAddressError", "DeliveryError"],
    dotColor: "bg-red-500",
  },
  {
    key: "Canceled",
    labelKey: "views.orders.filters.canceled",
    statuses: ["Canceled"],
    dotColor: "bg-gray-500",
  },
] as const;

/** Alias typed as PurchaseOrderStatus — use for order status comparisons */
export const DEFAULT_ORDER_STATUS: PurchaseOrderStatus = "WaitingToBeFulfilled";

/** Status that maps to "Pending validation" — rows get subdued tone */
export const PENDING_VALIDATION_STATUS: PurchaseOrderStatus = "AddressValidationError";

/** Shared Tailwind classes for clickable cell links (Order ID, Batch, Tracking) */
export const CELL_LINK_CLASSES = "text-[var(--link)] hover:text-[var(--link-hover)] hover:underline" as const;

// ---------------------------------------------------------------------------
// Age urgency thresholds (hours; negative = overdue)
// ---------------------------------------------------------------------------

export const AGE_CRITICAL_HOURS = -24;
export const AGE_WARNING_HOURS = -16;
export const AGE_YIELD_HOURS = -8;

// ---------------------------------------------------------------------------
// Selection / batch rules
// ---------------------------------------------------------------------------

/** Orders with these statuses cannot be selected for batch creation */
export const DISABLED_ORDER_STATUSES = new Set<PurchaseOrderStatus>([
  "Fulfilled",
  "Shipped",
  "Delivered",
  "AddressValidationError",
  "Canceled",
  "ZenkraftAddressError",
  "DeliveryError",
  "Error",
  "LabelError",
  "ProductError",
  "Publishing",
]);

// ---------------------------------------------------------------------------
// Shipping document status sets
// ---------------------------------------------------------------------------

/** Order statuses that disable all shipping document actions */
export const SHIPPING_DOC_DISABLING_ORDER_STATUSES = new Set<PurchaseOrderStatus>([
  "Error",
  "Delivered",
  "AddressValidationError",
  "Canceled",
  "ProductError",
  "ZenkraftAddressError",
]);

/** Order statuses that show the "Create Shipping Documents" button */
export const SHIPPING_DOC_CREATE_STATUSES = new Set<PurchaseOrderStatus>(["WaitingToBeFulfilled", "Publishing"]);

/** Batch statuses that disable shipping document actions */
export const SHIPPING_DOC_DISABLING_BATCH_STATUSES = new Set<LabelPrintBatchStatus>(["Processing", "Failed-for-Retry"]);

/** Order statuses that show the error notice banner */
export const ERROR_NOTICE_STATUSES = new Set<PurchaseOrderStatus>(["Error", "LabelError", "ZenkraftAddressError", "ProductError"]);

// Re-export from client-shared so consumers don't need a second import
export { MAX_PURCHASE_ORDER_CONNECTIONS_LIMIT } from "@client-shared/lib/constants";

/** Default page size for the orders data table */
export const DEFAULT_ORDERS_PAGE_SIZE = 50 as const;

// ---------------------------------------------------------------------------
// Shipping documents dialog tab IDs
// ---------------------------------------------------------------------------

export const SHIPPING_DOCUMENTS_TAB_IDS = {
  COMBINED: "combined",
  PACKING_SLIP: "packing-slip",
  SHIPPING_LABEL: "shipping-label",
  MANIFEST: "manifest",
} as const;

// ---------------------------------------------------------------------------
// Column IDs
// ---------------------------------------------------------------------------

export const ORDER_COLUMN_IDS = {
  SELECT: "select",
  ORDER_ID: "purchaseOrderId",
  CREATED_AT_STRING: "createdAtString",
  CREATED_AT: "createdAt",
  CREATED_AT_WITH_TIME: "createdAtWithTime",
  EXPECTED_FULFILLMENT_DATE: "expectedFulfillmentDate",
  AGE: "orderAge",
  STATUS: "orderStatus",
  BATCH: "batchId",
  MARKETPLACE: "marketplace",
  SHIP_TO_COUNTRY: "shipToCountry",
  SHIP_TO_FLAGS: "shipToFlags",
  ITEMS: "items",
  WEIGHT: "weight",
  ORDER_VALUE: "orderTotal",
  TRACKING: "tracking",
  SHIPPING_ADDRESS: "shippingAddress",
} as const;

// ---------------------------------------------------------------------------
// CSV export columns
// ---------------------------------------------------------------------------

export const ORDER_EXPORT_COLUMNS = [
  ORDER_COLUMN_IDS.ORDER_ID,
  ORDER_COLUMN_IDS.CREATED_AT_STRING,
  ORDER_COLUMN_IDS.STATUS,
  ORDER_COLUMN_IDS.BATCH,
  ORDER_COLUMN_IDS.MARKETPLACE,
  ORDER_COLUMN_IDS.SHIP_TO_COUNTRY,
  ORDER_COLUMN_IDS.ITEMS,
  ORDER_COLUMN_IDS.WEIGHT,
  ORDER_COLUMN_IDS.TRACKING,
] as const;

// ---------------------------------------------------------------------------
// All status keys re-export for convenience
// ---------------------------------------------------------------------------

export { PurchaseOrderStatusKeys };
